// src/components/resources/calculators/CPMCalculator.logic.ts

/**
 * Pure, framework-free CPM math — extracted from `CPMCalculator.tsx` so it can be
 * unit-tested directly (A7/D4 / BP1). The component imports `calculateMetrics`
 * and renders; all arithmetic lives here.
 */

export interface CalculatorInputs {
  adSpend: number
  impressions: number
  clicks?: number
  conversions?: number
}

export interface CalculatorResults {
  cpm: number
  ctr?: number
  cvr?: number
  cpc?: number
  cpa?: number
  effectiveCPM?: number
}

/**
 * Compute CPM and the derived click/conversion metrics.
 *
 * Optional metrics are emitted only when their inputs are present and positive:
 * - `ctr`/`cpc` require `clicks > 0`.
 * - `cvr`/`cpa`/`effectiveCPM` require both `clicks > 0` and `conversions > 0`
 *   (`effectiveCPM` guards against a zero conversion rate).
 */
export function calculateMetrics(inputs: CalculatorInputs): CalculatorResults {
  const results: CalculatorResults = {
    cpm: (inputs.adSpend / inputs.impressions) * 1000,
  }

  if (inputs.clicks && inputs.clicks > 0) {
    results.ctr = (inputs.clicks / inputs.impressions) * 100
    results.cpc = inputs.adSpend / inputs.clicks
  }

  if (inputs.conversions && inputs.conversions > 0 && inputs.clicks && inputs.clicks > 0) {
    results.cvr = (inputs.conversions / inputs.clicks) * 100
    results.cpa = inputs.adSpend / inputs.conversions
    // Handle potential division by zero
    const conversionRate = results.cvr ? results.cvr / 100 : 0
    results.effectiveCPM = conversionRate > 0 ? (inputs.adSpend / (inputs.impressions * conversionRate)) * 1000 : 0
  }

  return results
}
